import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteIcon from '@mui/icons-material/Delete';
import { Helmet } from 'react-helmet-async';
import { api } from '../../api/axios';
import type { User } from '../../types/domain';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useAuthStore } from '../../store/authStore';
import { useImpersonation } from '../../hooks/useImpersonation';

interface CreateUserDto {
  username: string;
  password: string;
  email?: string;
  displayName: string;
  role: 'UNIT_ADMIN' | 'RECORDER';
  unitId?: number | null;
}

const ROLE_LABELS = {
  SUPER_ADMIN: 'Super Admin',
  UNIT_ADMIN: 'Admin jednotky',
  RECORDER: 'Zapisovatel',
} as const;

const adminUsersApi = {
  list: (unitId?: number | null) =>
    api
      .get<User[]>('/admin/users', { params: unitId ? { unitId } : undefined })
      .then((r) => r.data),
  create: (dto: CreateUserDto) => api.post<User>('/admin/users', dto).then((r) => r.data),
  resetPassword: (id: number, newPassword: string) =>
    api.post(`/admin/users/${id}/reset-password`, { newPassword }),
  deactivate: (id: number) => api.delete(`/admin/users/${id}`),
};

export const AdminUsersPage = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useAuthStore((s) => s.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { effectiveUnitId, isImpersonating, impersonatedUnit } = useImpersonation();
  const [creating, setCreating] = useState(false);
  const [resetPwd, setResetPwd] = useState<User | null>(null);
  const [deactivating, setDeactivating] = useState<User | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin', 'users', effectiveUnitId ?? 'all'],
    queryFn: () => adminUsersApi.list(effectiveUnitId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });

  const createMutation = useMutation({
    mutationFn: (dto: CreateUserDto) =>
      adminUsersApi.create({
        ...dto,
        unitId: effectiveUnitId ?? dto.unitId ?? null,
      }),
    onSuccess: () => {
      enqueueSnackbar('Uživatel vytvořen', { variant: 'success' });
      invalidate();
      setCreating(false);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const resetMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      adminUsersApi.resetPassword(id, password),
    onSuccess: () => {
      enqueueSnackbar('Heslo změněno', { variant: 'success' });
      setResetPwd(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => adminUsersApi.deactivate(id),
    onSuccess: () => {
      enqueueSnackbar('Uživatel deaktivován', { variant: 'success' });
      invalidate();
      setDeactivating(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const columns: GridColDef<User>[] = [
    { field: 'username', headerName: 'Username', flex: 1, minWidth: 140 },
    { field: 'displayName', headerName: 'Jméno', flex: 1, minWidth: 180 },
    { field: 'email', headerName: 'E-mail', flex: 1, minWidth: 180 },
    {
      field: 'role',
      headerName: 'Role',
      width: 160,
      renderCell: ({ row }) => (
        <Chip
          label={ROLE_LABELS[row.role]}
          size="small"
          color={row.role === 'UNIT_ADMIN' ? 'primary' : 'default'}
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Stav',
      width: 110,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.isActive ? 'Aktivní' : 'Neaktivní'}
          color={row.isActive ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row">
          <Tooltip title="Reset hesla">
            <IconButton size="small" onClick={() => setResetPwd(row)}>
              <LockResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.id === currentUser?.id ? 'Nelze deaktivovat sami sebe' : 'Deaktivovat'}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!row.isActive || row.id === currentUser?.id}
                onClick={() => setDeactivating(row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Uživatelé jednotky | Admin</title>
      </Helmet>
      <PageHeader
        title="Uživatelé jednotky"
        subtitle={
          isImpersonating
            ? `Admini a zapisovatelé jednotky ${impersonatedUnit?.name ?? ''}`
            : 'Admini a zapisovatelé vaší jednotky'
        }
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreating(true)}>
            Nový uživatel
          </Button>
        }
      />
      {isMobile ? (
        <Stack spacing={1}>
          {isLoading && <Typography color="text.secondary">Načítám…</Typography>}
          {!isLoading && users.length === 0 && (
            <Typography color="text.secondary">Žádní uživatelé.</Typography>
          )}
          {users.map((u) => (
            <Paper key={u.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={600}>{u.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {u.username}
                    {u.email && ` • ${u.email}`}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip
                      label={ROLE_LABELS[u.role]}
                      size="small"
                      color={u.role === 'UNIT_ADMIN' ? 'primary' : 'default'}
                    />
                    <Chip
                      size="small"
                      label={u.isActive ? 'Aktivní' : 'Neaktivní'}
                      color={u.isActive ? 'success' : 'default'}
                    />
                  </Stack>
                </Box>
                <Stack direction="row" sx={{ flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => setResetPwd(u)}>
                    <LockResetIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!u.isActive || u.id === currentUser?.id}
                    onClick={() => setDeactivating(u)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 'calc(100vh - 220px)', bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid rows={users} columns={columns} loading={isLoading} disableRowSelectionOnClick sx={{ border: 0 }} />
        </Box>
      )}

      <CreateUserDialog
        open={creating}
        loading={createMutation.isPending}
        onCancel={() => setCreating(false)}
        onSubmit={(dto) => createMutation.mutate(dto)}
      />

      <ResetPasswordDialog
        open={!!resetPwd}
        user={resetPwd}
        loading={resetMutation.isPending}
        onCancel={() => setResetPwd(null)}
        onSubmit={(password) => resetPwd && resetMutation.mutate({ id: resetPwd.id, password })}
      />

      <ConfirmDialog
        open={!!deactivating}
        title="Deaktivovat uživatele?"
        message={`Uživatel "${deactivating?.displayName}" bude označen jako neaktivní.`}
        destructive
        loading={deactivateMutation.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => deactivating && deactivateMutation.mutate(deactivating.id)}
      />
    </>
  );
};

interface CreateUserDialogProps {
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateUserDto) => void;
}

const CreateUserDialog = ({ open, loading, onCancel, onSubmit }: CreateUserDialogProps) => {
  const [form, setForm] = useState<CreateUserDto>({
    username: '',
    password: '',
    displayName: '',
    email: '',
    role: 'RECORDER',
  });
  const update = <K extends keyof CreateUserDto>(field: K, value: CreateUserDto[K]) =>
    setForm((f) => ({ ...f, [field]: value }));
  const reset = () =>
    setForm({ username: '', password: '', displayName: '', email: '', role: 'RECORDER' });

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth TransitionProps={{ onEnter: reset }}>
      <DialogTitle>Nový uživatel</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Username" value={form.username} onChange={(e) => update('username', e.target.value)} required autoFocus />
          <TextField label="Zobrazované jméno" value={form.displayName} onChange={(e) => update('displayName', e.target.value)} required />
          <TextField label="E-mail" type="email" value={form.email ?? ''} onChange={(e) => update('email', e.target.value)} />
          <TextField label="Heslo" type="password" value={form.password} onChange={(e) => update('password', e.target.value)} required helperText="Alespoň 8 znaků" />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={form.role}
              label="Role"
              onChange={(e) => update('role', e.target.value as 'UNIT_ADMIN' | 'RECORDER')}
            >
              <MenuItem value="UNIT_ADMIN">Admin jednotky</MenuItem>
              <MenuItem value="RECORDER">Zapisovatel</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(form)}
          disabled={loading || !form.username || !form.password || !form.displayName || form.password.length < 8}
        >
          Vytvořit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ResetPasswordDialogProps {
  open: boolean;
  user: User | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (password: string) => void;
}

const ResetPasswordDialog = ({ open, user, loading, onCancel, onSubmit }: ResetPasswordDialogProps) => {
  const [password, setPassword] = useState('');
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth TransitionProps={{ onEnter: () => setPassword('') }}>
      <DialogTitle>Reset hesla</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Uživatel" value={user?.displayName ?? ''} disabled />
          <TextField
            label="Nové heslo"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            helperText="Alespoň 8 znaků"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Zrušit</Button>
        <Button variant="contained" onClick={() => onSubmit(password)} disabled={loading || password.length < 8}>
          Změnit heslo
        </Button>
      </DialogActions>
    </Dialog>
  );
};
